import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { deletionReferences } from "@wedding/api-client";
import type { EntityRecord, EntityType } from "@wedding/schemas";
import { Button, Skeleton } from "@wedding/ui";
import { ClipboardPenLine, Filter, Plus, Search, X } from "lucide-react";
import { api, apiFetch } from "../api";
import { modules, type ModuleConfig, type TaskModuleType } from "../config";
import { TaskLibraryPage } from "../pages/TaskLibraryPage";
import { EntityEditor } from "./EntityEditor";
import { EntityDetail, type ProjectMemberIdentity } from "./EntityDetail";
import { EntityTable } from "./EntityTable";
import { TaskBulkEditor } from "./TaskBulkEditor";
import { sortTasksByStatus, taskOwners } from "../task-list";

const taskConfig = modules.find((module) => module.type === "tasks")!;

export function ModuleTodoSection({
  workspaceId,
  moduleType,
  moduleLabel,
  view = "current",
}: {
  workspaceId: string;
  moduleType: TaskModuleType;
  moduleLabel: string;
  view?: "current" | "library";
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set<string>());
  const [editing, setEditing] = useState<EntityRecord>();
  const [viewing, setViewing] = useState<EntityRecord>();
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<EntityRecord>();
  const [filters, setFilters] = useState({
    status: "",
    owner: "",
    vendorId: "",
  });
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const config = useMemo<ModuleConfig>(
    () => ({
      ...taskConfig,
      label: `${moduleLabel}待办`,
      singular: `${moduleLabel}待办`,
      description: `仅显示归属「${moduleLabel}」的待办，与总任务清单共用同一条数据。`,
      fields: taskConfig.fields.filter((field) => field.key !== "moduleType"),
    }),
    [moduleLabel],
  );

  const tasks = useQuery({
    queryKey: [workspaceId, "tasks"],
    queryFn: () => api.list(workspaceId, "tasks").then((result) => result.data),
  });
  const vendors = useQuery({
    queryKey: [workspaceId, "vendors"],
    queryFn: () =>
      api.list(workspaceId, "vendors").then((result) => result.data),
  });
  const members = useQuery({
    queryKey: [workspaceId, "members"],
    queryFn: () =>
      apiFetch<{ members: ProjectMemberIdentity[] }>(
        `/workspaces/${workspaceId}/members`,
      ),
  });
  const items = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return sortTasksByStatus(
      (tasks.data ?? []).filter(
        (task) =>
          task.data.moduleType === moduleType &&
          (!keyword ||
            JSON.stringify(task.data).toLowerCase().includes(keyword)) &&
          (!filters.status || task.data.status === filters.status) &&
          (!filters.owner || task.data.owner === filters.owner) &&
          (!filters.vendorId || task.data.vendorId === filters.vendorId),
      ),
    );
  }, [filters, moduleType, search, tasks.data]);
  const owners = useMemo(() => taskOwners(tasks.data ?? []), [tasks.data]);
  const relationOptions = useMemo(
    () => ({ vendors: vendors.data ?? [] }),
    [vendors.data],
  );
  const relationLabels = useMemo(
    () => ({
      vendorId: Object.fromEntries(
        (vendors.data ?? []).map((vendor) => [
          vendor.id,
          [vendor.data.vendorName, vendor.data.vendorType]
            .filter(Boolean)
            .join(" · "),
        ]),
      ),
    }),
    [vendors.data],
  );

  const save = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      editing
        ? api.update(
            workspaceId,
            "tasks",
            editing.id,
            { ...data, moduleType },
            editing.version,
          )
        : api.create(workspaceId, "tasks", {
            ...data,
            moduleType,
            status: data.status || "未开始",
          }),
    onSuccess: (result) => {
      queryClient.setQueryData<EntityRecord[]>([workspaceId, "tasks"], (old) =>
        editing
          ? old?.map((task) => (task.id === editing.id ? result.data : task))
          : [result.data, ...(old ?? [])],
      );
      setEditing(undefined);
      setOpen(false);
    },
  });
  const remove = useMutation({
    mutationFn: ({ task, cascade }: { task: EntityRecord; cascade: boolean }) =>
      api.remove(workspaceId, "tasks", task.id, task.version, cascade),
    onSuccess: (_, { task, cascade }) => {
      queryClient.setQueryData<EntityRecord[]>([workspaceId, "tasks"], (old) =>
        old?.filter((item) => item.id !== task.id),
      );
      if (cascade)
        void queryClient.invalidateQueries({ queryKey: [workspaceId] });
      setDeleting(undefined);
    },
  });
  const deleteReferences = deletionReferences(remove.error);
  const bulkUpdate = useMutation({
    mutationFn: async (patch: Record<string, unknown>) => {
      const targets = items.filter((item) => selected.has(item.id));
      const updated: EntityRecord[] = [];
      for (const item of targets) {
        const result = await api.update(
          workspaceId,
          "tasks",
          item.id,
          patch,
          item.version,
        );
        updated.push(result.data);
      }
      return updated;
    },
    onSuccess: (updated) => {
      const byId = new Map(updated.map((item) => [item.id, item]));
      queryClient.setQueryData<EntityRecord[]>([workspaceId, "tasks"], (old) =>
        old?.map((item) => byId.get(item.id) ?? item),
      );
      setSelected(new Set());
      setBulkEditOpen(false);
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: [workspaceId, "tasks"] }),
  });

  const createRelation = async (
    type: EntityType,
    data: Record<string, unknown>,
  ) => {
    const result = await api.create(workspaceId, type, data);
    queryClient.setQueryData<EntityRecord[]>(
      [workspaceId, "vendors"],
      (old) => [result.data, ...(old ?? [])],
    );
    return result.data;
  };

  return (
    <section
      className="module-todos integrated"
      aria-label={`${moduleLabel}待办`}
    >
      {view === "library" ? (
        <TaskLibraryPage moduleType={moduleType} embedded />
      ) : (
        <>
          <div className="module-task-toolbar">
            <div className="search-box">
              <Search size={17} />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={`搜索${moduleLabel}事项…`}
              />
            </div>
            <label className="filter-select">
              <Filter size={16} />
              <select
                aria-label="按状态筛选筹备事项"
                value={filters.status}
                onChange={(event) => {
                  setFilters((current) => ({
                    ...current,
                    status: event.target.value,
                  }));
                  setSelected(new Set());
                }}
              >
                <option value="">全部状态</option>
                {["未开始", "进行中", "待确认", "已完成", "延期", "已取消"].map(
                  (status) => (
                    <option key={status}>{status}</option>
                  ),
                )}
              </select>
            </label>
            <label className="filter-select">
              <Filter size={16} />
              <select
                aria-label="按负责人筛选筹备事项"
                value={filters.owner}
                onChange={(event) => {
                  setFilters((current) => ({
                    ...current,
                    owner: event.target.value,
                  }));
                  setSelected(new Set());
                }}
              >
                <option value="">全部负责人</option>
                {owners.map((owner) => (
                  <option key={owner}>{owner}</option>
                ))}
              </select>
            </label>
            <label className="filter-select">
              <Filter size={16} />
              <select
                aria-label="按关联供应商筛选筹备事项"
                value={filters.vendorId}
                onChange={(event) => {
                  setFilters((current) => ({
                    ...current,
                    vendorId: event.target.value,
                  }));
                  setSelected(new Set());
                }}
              >
                <option value="">全部关联供应商</option>
                {(vendors.data ?? []).map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>
                    {String(vendor.data.vendorName ?? "未命名供应商")}
                  </option>
                ))}
              </select>
            </label>
            {selected.size > 0 && (
              <button
                className="secondary-action module-bulk-edit"
                onClick={() => setBulkEditOpen(true)}
              >
                <ClipboardPenLine size={16} /> 批量编辑 {selected.size} 项
              </button>
            )}
            <Button
              onClick={() => {
                setEditing(undefined);
                setOpen(true);
              }}
            >
              <Plus size={17} /> 新建事项
            </Button>
          </div>
          <div className="data-panel module-task-panel">
            {tasks.isLoading ? (
              <Skeleton lines={6} />
            ) : (
              <EntityTable
                config={config}
                items={items}
                selected={selected}
                relationLabels={relationLabels}
                onSelected={setSelected}
                onCreate={() => {
                  setEditing(undefined);
                  setOpen(true);
                }}
                onView={setViewing}
                onEdit={(task) => {
                  setEditing(task);
                  setOpen(true);
                }}
                onDelete={(task) => {
                  remove.reset();
                  setDeleting(task);
                }}
              />
            )}
          </div>
        </>
      )}
      <EntityEditor
        config={config}
        entity={editing}
        open={open}
        saving={save.isPending}
        relationOptions={relationOptions}
        onCreateRelation={createRelation}
        onClose={() => setOpen(false)}
        onSave={(data) => save.mutate(data)}
      />
      <TaskBulkEditor
        open={bulkEditOpen}
        count={items.filter((item) => selected.has(item.id)).length}
        saving={bulkUpdate.isPending}
        vendors={vendors.data ?? []}
        onClose={() => setBulkEditOpen(false)}
        onSave={(patch) => bulkUpdate.mutate(patch)}
      />
      {viewing && (
        <EntityDetail
          item={viewing}
          config={config}
          relationLabels={relationLabels}
          members={members.data?.members ?? []}
          onClose={() => setViewing(undefined)}
          onEdit={() => {
            setEditing(viewing);
            setViewing(undefined);
            setOpen(true);
          }}
        />
      )}
      {deleting && (
        <div className="drawer-backdrop">
          <aside
            className="drawer"
            role="dialog"
            aria-modal="true"
            aria-label="删除待办"
          >
            <header>
              <div>
                <span className="eyebrow">删除事项</span>
                <h2>删除这条待办？</h2>
              </div>
              <button
                className="icon-button"
                aria-label="关闭"
                onClick={() => {
                  remove.reset();
                  setDeleting(undefined);
                }}
              >
                <X />
              </button>
            </header>
            <div className="confirmation-content">
              {deleteReferences.length ? (
                <div className="dependency-warning" role="alert">
                  <strong>
                    这条待办还出现在以下 {deleteReferences.length} 条数据中：
                  </strong>
                  <ul>
                    {deleteReferences.map((reference) => (
                      <li key={`${reference.entityType}:${reference.entityId}`}>
                        <span>{reference.moduleLabel}</span>
                        <b>「{reference.recordLabel}」</b>
                        <small>{reference.relationLabel}</small>
                      </li>
                    ))}
                  </ul>
                  <p>再次确认后，上述关联数据也会一并删除。</p>
                </div>
              ) : (
                <p>
                  “{String(deleting.data.title ?? "未命名待办")}
                  ”会同时从本模块和总任务清单中删除。
                </p>
              )}
              {remove.error && !deleteReferences.length && (
                <p className="form-error">{remove.error.message}</p>
              )}
            </div>
            <footer>
              <Button
                className="secondary"
                onClick={() => {
                  remove.reset();
                  setDeleting(undefined);
                }}
              >
                取消
              </Button>
              <Button
                className="danger"
                onClick={() =>
                  remove.mutate({
                    task: deleting,
                    cascade: deleteReferences.length > 0,
                  })
                }
                disabled={remove.isPending}
              >
                {remove.isPending
                  ? "删除中…"
                  : deleteReferences.length
                    ? "确认并一并删除"
                    : "确认删除"}
              </Button>
            </footer>
          </aside>
        </div>
      )}
      {save.error && <div className="toast error">{save.error.message}</div>}
      {bulkUpdate.error && (
        <div className="toast error">{bulkUpdate.error.message}</div>
      )}
    </section>
  );
}
